import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Download, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTemplatesQuery } from '@/lib/api/methodology-library';
import { LibraryGrid } from '@/components/settings/library-grid';

/**
 * Settings → Methodology library (T-0111 / E01-F04 + F09, C20-121).
 *
 * Retired (C2-03). ADR-022 decision 5 removed the methodology-template
 * library as a prerequisite: assessment defaults replaced it, so this
 * address now lands on Firm settings > Assessment defaults. The screen
 * below and `GET /api/v1/templates` stay in place for the surfaces that
 * still read them (`LibraryGrid`, the widget editor); nothing links here.
 *
 * Before that, this rendered the firm template library —
 * frameworks, framework overlays, register schemas, and widget defaults
 * — grouped by kind. Mirrors `design_handoff_caliber_v2/prototype/
 * screens-firm.js → firmLibrary()` (see also README §4 row "library").
 *
 * Data: T-0025 backend (`GET /api/v1/templates` + `…/:id/versions`). The
 * `include_retired` query param flips when the consultant toggles the
 * "Show retired" checkbox in the header. Search is a client-side filter
 * over template names — no server-side search endpoint exists yet.
 *
 * "New template" / "Export bundle" / "Edit" affordances ship as stubs
 * here; the curation-write flows (author + retire) and the widget-
 * defaults editor modal (T-0114) come in a later wave. The Edit button
 * on each framework card carries the framework-version id forward via
 * `data-open-widgets={frameworkVersionId}` so the eventual T-0114
 * modal can wire to it without re-plumbing.
 */
export const LIBRARY_REDIRECT_TO = '/settings/assessment-defaults' as const;

export const Route = createFileRoute('/settings/library')({
  beforeLoad: () => {
    throw redirect({ to: LIBRARY_REDIRECT_TO, replace: true });
  },
  component: MethodologyLibraryScreen,
});

export function MethodologyLibraryScreen() {
  const [includeRetired, setIncludeRetired] = useState(false);
  const [search, setSearch] = useState('');

  // Subtitle uses an unfiltered count so the user always sees the firm's
  // total inventory regardless of the in-screen search filter.
  const totalsQuery = useTemplatesQuery({ includeRetired });
  const total = totalsQuery.data?.length ?? 0;
  const retired = totalsQuery.data?.filter((t) => t.status === 'retired').length ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Methodology library</h1>
          <p className="text-meta text-ink-500">
            {total} template{total === 1 ? '' : 's'}
            {includeRetired && retired > 0 ? ` · ${retired} retired` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex h-9 items-center gap-2 rounded border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-700 focus-within:ring-2 focus-within:ring-accent-amber-600">
            <Search className="h-3.5 w-3.5 text-ink-500" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates…"
              className="h-full w-44 bg-transparent text-body-sm text-ink-700 placeholder:text-ink-300 focus:outline-none"
              aria-label="Search templates"
            />
          </label>
          <label className="inline-flex h-9 items-center gap-2 text-meta text-ink-500">
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={(e) => setIncludeRetired(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-ink-200 text-accent-amber-600 focus:ring-accent-amber-600"
            />
            <span>Show retired</span>
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled
            title="Bundle export ships in a later wave."
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Export bundle
          </Button>
          <Button type="button" size="sm" disabled title="Authoring UI ships in a later wave.">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New template
          </Button>
        </div>
      </div>

      <LibraryGrid
        includeRetired={includeRetired}
        searchFilter={search}
        onEditWidgets={(frameworkVersionId) => {
          // T-0114 will mount a widget-defaults editor modal on this id.
          // For v1 we surface a console hint so the click isn't silent
          // when the modal isn't wired yet.
          // eslint-disable-next-line no-console
          console.info(
            '[T-0111] Widget defaults editor (T-0114) not yet wired — framework version:',
            frameworkVersionId,
          );
        }}
      />
    </div>
  );
}
